import { Box, Group, Progress, Stack, Text } from '@mantine/core';
import { BarChart3, CalendarClock, Clock } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { Badge, Card } from '../ui';
import { rowItem } from '../../lib/motion';
import { categoryIcon, categoryLabel, DIFFICULTY_META } from './labMeta';
import type { LabSummary } from '../../api/types';

interface AssignmentInfo {
  dueAt?: string | null;
  required?: boolean;
}

// Deterministic hue from the slug so a lab always gets the same thumbnail colour.
function hueFromString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

/**
 * A single lab as a full-width list row — thumbnail, title, tags, difficulty +
 * time, description, and a progress readout on the right. Shared by the staff
 * catalog and the trainee "assigned to me" list so both read identically.
 */
export function LabRow({
  lab,
  assignment,
  completed,
}: {
  lab: LabSummary;
  assignment?: AssignmentInfo;
  completed?: number;
}) {
  const diff = DIFFICULTY_META[lab.difficulty];
  const hue = hueFromString(lab.slug || lab.title);
  const overdue = assignment?.dueAt ? new Date(assignment.dueAt).getTime() < Date.now() : false;
  const pct = lab.taskCount ? Math.round(((completed ?? 0) / lab.taskCount) * 100) : 0;
  const CatIcon = categoryIcon(lab.category);

  return (
    <motion.div variants={rowItem}>
      <Link
        to="/labs/$id"
        params={{ id: lab.slug }}
        preload="intent"
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <Card hoverable>
          <Group gap="md" wrap="nowrap" align="flex-start">
            <Box
              style={{
                flexShrink: 0,
                width: 56,
                height: 56,
                borderRadius: 12,
                background: `hsl(${hue} 45% 22% / 0.55)`,
                border: `1px solid hsl(${hue} 50% 45% / 0.4)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CatIcon size={26} color={`hsl(${hue} 70% 62%)`} strokeWidth={1.75} />
            </Box>

            <Stack gap={5} style={{ flex: 1, minWidth: 0 }}>
              <Group gap={8} wrap="wrap" align="center">
                <Text fw={700} size="sm" c="rgb(var(--c-txt-bright))">{lab.title}</Text>
                {assignment?.required ? <Badge variant="info" size="xs">required</Badge> : null}
              </Group>
              <Group gap="lg" wrap="wrap">
                <Text size="xs" c="dimmed">{categoryLabel(lab.category)}</Text>
                <Group gap={4} wrap="nowrap"><BarChart3 size={12} /><Text size="xs" c="dimmed">{diff.label}</Text></Group>
                <Group gap={4} wrap="nowrap"><Clock size={12} /><Text size="xs" c="dimmed">{lab.estimatedMins}m</Text></Group>
                {lab.points ? <Text size="xs" c="dimmed">{lab.points} pts</Text> : null}
                {assignment?.dueAt ? (
                  <Group gap={4} wrap="nowrap">
                    <CalendarClock size={12} color={overdue ? '#e5484d' : undefined} />
                    <Text size="xs" c={overdue ? 'red' : 'dimmed'}>
                      {overdue ? 'Overdue' : `Due ${new Date(assignment.dueAt).toLocaleDateString()}`}
                    </Text>
                  </Group>
                ) : null}
              </Group>
              {lab.description ? (
                <Text size="sm" c="dimmed" lineClamp={2}>{lab.description}</Text>
              ) : null}
            </Stack>

            <Stack gap={4} align="flex-end" style={{ flexShrink: 0, width: 116 }} visibleFrom="sm">
              <Text size="xs" c="dimmed" className="font-mono">{completed ?? 0}/{lab.taskCount}</Text>
              <Progress value={pct} size="sm" radius="xl" color="teal" style={{ width: 116 }} />
            </Stack>
          </Group>
        </Card>
      </Link>
    </motion.div>
  );
}
